import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IServiceOverrideResponse, ServiceOverrideType, IStatusResponse } from '@bciti/types';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { UpdateServiceAction, UpdateServiceErrorAction, UpdateServiceSuccessAction } from 'src/app/global/store/global.actions';
import { organisationConfigurationSelector } from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';
import * as uuid from 'uuid';
import { Service } from 'src/app/global/services/service-state/service-state.model';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';

// TODO: Fix @bciit/types
interface LocalIStatusResponse extends IStatusResponse {
    _id: string;
}

interface LocalIServiceOverrideResponse extends IServiceOverrideResponse {
    status: LocalIStatusResponse;
}

@Component({
    selector: 'app-service-manual-status-change-dialog',
    templateUrl: './service-manual-status-change-dialog.component.html',
    styleUrls: ['./service-manual-status-change-dialog.component.scss'],
})
export class ServiceManualStatusChangeDialogComponent implements OnInit {
    private destroy$ = new Subject();

    processingSubmit = false;
    langs: string[];
    id: string;
    serviceName: string;
    manualStatusChangeForm: any = new FormGroup({});

    statusList = [];

    scheduleOptionList = [
        {
            label: this.translate.instant('pages.services-state.dialogs.manual-status-change-dialog.schedule.automatic'),
            value: ServiceOverrideType.AUTOMATIC,
        },
        {
            label: this.translate.instant('pages.services-state.dialogs.manual-status-change-dialog.schedule.manual'),
            value: ServiceOverrideType.MANUAL,
        },
    ];

    characterLimit = 50;
    disableDateAndHour: boolean;
    startDateMin = new Date();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { service: Service; tag: string },
        private dialogRef: MatDialogRef<ServiceManualStatusChangeDialogComponent>,
        private translate: TranslateService,
        private fb: FormBuilder,
        private store: Store<State>,
        private actions$: Actions,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.id = uuid.v4();

        this.statusList = [
            ...[
                {
                    value: this.data.service.availableStatus._id,
                    label: this.data.service.availableStatus.name[this.translate.currentLang],
                },
                {
                    value: this.data.service.nonAvailableStatus._id,
                    label: this.data.service.nonAvailableStatus.name[this.translate.currentLang],
                },
            ],
        ];

        if (this.data.tag === 'open-status') {
            this.statusControl.patchValue(this.statusList[0]);
        } else {
            this.statusControl.patchValue(this.statusList[1]);
        }

        this.actions$
            .pipe(
                ofType(UpdateServiceAction),
                tap(() => (this.processingSubmit = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                ofType(UpdateServiceSuccessAction, UpdateServiceErrorAction),
                tap(() => (this.processingSubmit = false)),
                ofType(UpdateServiceSuccessAction),
                tap(() => this.close()),
            )
            .subscribe();

        this.store
            .select(organisationConfigurationSelector)
            .pipe(
                takeUntil(this.destroy$),
                filter((config) => !!config),
                map((config) => {
                    this.langs = config?.lang?.available ?? [];
                    this.langs.forEach((lang) => {
                        (this.manualStatusChangeForm.get('reason') as unknown as FormGroup).addControl(lang, new FormControl('', [Validators.required]));
                        if (typeof this.characterLimit === 'number' && this.characterLimit > 0) {
                            (this.manualStatusChangeForm.get('reason') as unknown as FormGroup).controls[lang].setValidators([Validators.required, Validators.maxLength(this.characterLimit)]);
                            (this.manualStatusChangeForm.get('reason') as unknown as FormGroup).controls[lang].updateValueAndValidity();
                        }
                    });
                    // set this.langs array order to have currentLang as first index
                    this.langs = [this.currentLang, ...this.langs.filter((l) => this.currentLang !== l)];
                }),
            )
            .subscribe();

        this.overrideControl.valueChanges.subscribe((override) => {
            if (override.value !== ServiceOverrideType.AUTOMATIC) {
                this.setNullDateAndTimeValues();
            }
            if (override.value === ServiceOverrideType.AUTOMATIC) {
                this.disableDateAndHour = false;
                this.dateControl.setValidators([Validators.required]);
                this.timeControl.setValidators([Validators.required]);
                this.dateControl.updateValueAndValidity();
                this.timeControl.updateValueAndValidity();
                this.manualStatusChangeForm.updateValueAndValidity();
            }
        });
        this.populateForm();
    }

    initForm() {
        this.serviceName = this.data.service.name[this.translate.currentLang];
        this.manualStatusChangeForm = this.fb.group({
            status: new FormControl('', [Validators.required]),
            reason: this.fb.group({}),
            override: new FormControl('', [Validators.required]),
            date: new FormControl(''),
            time: new FormControl(''),
        });

        if (this.overrideControl.value === ServiceOverrideType.AUTOMATIC) {
            this.disableDateAndHour = false;
            this.dateControl.setValidators([Validators.required]);
            this.timeControl.setValidators([Validators.required]);
        } else {
            this.setNullDateAndTimeValues();
        }
    }

    populateForm(): void {
        const overrideData: LocalIServiceOverrideResponse = this.data.service.override as LocalIServiceOverrideResponse;
        if (!overrideData) return;

        // if automatic and overrideEnd is in the past, do not populate form
        if (overrideData.type === ServiceOverrideType.AUTOMATIC && new Date() > new Date(overrideData.overrideEnd)) return;

        this.langs.forEach((lang) => {
            let reasonValue = overrideData.reason[lang] ?? overrideData.reason;
            (this.reasonControl as FormGroup).controls[lang].setValue(reasonValue);
        });

        this.overrideControl.patchValue({
            value: overrideData.type,
            label: this.scheduleOptionList.find((option) => option.value === overrideData.type).label,
        });

        if (overrideData.type === ServiceOverrideType.AUTOMATIC) {
            const date = new Date(overrideData.overrideEnd);
            const timeValue = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            this.dateControl.setValue(date);
            this.timeControl.setValue(timeValue);
            this.manualStatusChangeForm.updateValueAndValidity();
        }
    }

    setNullDateAndTimeValues() {
        this.dateControl.clearValidators();
        this.timeControl.clearValidators();
        this.manualStatusChangeForm.patchValue({ date: null });
        this.manualStatusChangeForm.patchValue({ time: null });
        this.dateControl.updateValueAndValidity();
        this.timeControl.updateValueAndValidity();
        this.manualStatusChangeForm.updateValueAndValidity();
        this.disableDateAndHour = true;
    }

    hasError(lang: string) {
        return (this.reasonControl as FormGroup).controls[lang].invalid && ((this.reasonControl as FormGroup).controls[lang].touched || (this.reasonControl as FormGroup).controls[lang].dirty);
    }

    hasRequiredError(lang: string) {
        return (
            (this.reasonControl as FormGroup).controls[lang].getError('required') &&
            ((this.reasonControl as FormGroup).controls[lang].touched || (this.reasonControl as FormGroup).controls[lang].dirty)
        );
    }

    dateChange(e: Date) {
        this.manualStatusChangeForm.patchValue({ date: e });
    }

    submit(): void {
        if (!this.manualStatusChangeForm.valid) {
            this.manualStatusChangeForm.markAllAsTouched();
            return;
        }

        const payload: any = {
            _id: this.data.service._id,
            override: {
                reason: this.reasonControl.value,
                type: this.overrideControl.value.value,
                status: this.statusControl.value.value,
            },
        };

        if (this.overrideControl.value.value === ServiceOverrideType.AUTOMATIC) {
            const mappedDate = this.mapDateAndTime(this.manualStatusChangeForm.controls.date.value, this.manualStatusChangeForm.controls.time.value);
            payload.override.overrideEnd = mappedDate;
        }

        this.store.dispatch(UpdateServiceAction({ service: payload }));
    }

    mapDateAndTime(date: string, time: string): string {
        const dateValue = new Date(date);
        const timeValue = time;
        const offsetMinutes = new Date().getTimezoneOffset();
        const offsetHours = Math.abs(offsetMinutes / 60);
        const offsetSign = offsetMinutes < 0 ? '+' : '-';
        const formattedOffset = `${offsetSign}${String(offsetHours).padStart(2, '0')}:00`;
        const isoDate = dateValue.toISOString().split('T')[0];
        const formattedDateTime = `${isoDate}T${timeValue}:00.000${formattedOffset}`;
        return formattedDateTime;
    }

    cancel(): void {
        this.close();
    }

    close() {
        this.dialogRef.close();
    }

    /** GETTERS */

    get currentLang(): string {
        return this.translate.currentLang;
    }

    get statusControl() {
        return this.manualStatusChangeForm.controls.status;
    }

    get dateControl() {
        return this.manualStatusChangeForm.controls.date;
    }

    get reasonControl() {
        return this.manualStatusChangeForm.controls.reason;
    }

    get timeControl() {
        return this.manualStatusChangeForm.controls.time;
    }

    get overrideControl() {
        return this.manualStatusChangeForm.controls.override;
    }
}
